'use client';
import Link from 'next/link';
import { cn } from '@pulsex/utils';
import { Category } from '@/ui/Category/Index';
import { Summary } from '@/ui/Summary/Index';
import ImageUI from '@/ui/Image/Index';
import NavControl from '@/ui/NavControl/Index';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { MAIN_TERM_PATHS_ARRAY } from '@/constants';
import { getNewspaperDate } from '@/utils/getNewspaperDate';
import { formatArabicDate } from '@/utils/formatDate';
import Author from '@/ui/Author/Index';
import { Time } from '@/ui/Time/Index';
import { postHref } from '@/utils/links';

interface HeroProps {
    data: any;
    isMobile?: boolean;
    handlePrevious?: () => void;
    handleNext?: () => void;
    setActive: (index: number) => void;
    className?: string;
    template?: string;
    priority?: boolean;
    colorTheme?: string;
    widgetTitle?: string;
    length?: number;
    currentIndex?: number;
    setCurrentIndex?: any;
    customText?: string;
    resolution?: number;
}

export const Hero = ({
    data,
    isMobile,
    setActive,
    currentIndex,
    setCurrentIndex,
    className,
    template,
    priority,
    colorTheme,
    widgetTitle,
    length,
    customText,
    resolution,
}: HeroProps) => {
    const useMainTerm = (pathname: string) => {
        return MAIN_TERM_PATHS_ARRAY.indexOf(pathname) !== -1;
    };

    const pathname = usePathname();
    const decodedPathname = useMemo(() => decodeURI(pathname), [pathname]);
    const isMainTerm = useMainTerm(decodedPathname);
    const terms = data?.terms ?? [];
    const forceTerm = (() => {
        if (terms[0]?.title === 'على الغلاف') {
            return terms[2];
        }

        if (
            terms.length > 2 &&
            ((terms[0]?.title === 'عرب' &&
                terms[1]?.title !== 'فلسطين' &&
                terms[1]?.title !== 'سوريا') ||
                terms[0]?.title === 'عالم')
        ) {
            return terms[2];
        }

        if (terms[0]?.title === 'سوريا') {
            return terms[0];
        }

        if (terms.length < 3 && terms[1]?.title === 'حياة وناس') {
            return terms[0];
        }

        return terms[1] ?? terms[0];
    })();

    const dateValue = getNewspaperDate(data);
    const dateToShow = useMemo(() => {
        try {
            let date: string | undefined;
            if (data?.publishDate && !dateValue) {
                date = formatArabicDate({
                    arabicDate: data.publishDate,
                    needDay: false,
                });
            } else if (data?.publishDate && dateValue) {
                date = formatArabicDate({
                    arabicDate: dateValue || data.publishDate,
                    needDay: false,
                });
            }
            return date;
        } catch (error) {
            console.error('Failed to format date:', error);
            return dateValue || data?.displayDate || data?.publishDate;
        }
    }, [data, dateValue]);

    if (!data) return;

    const TermValue =
        data?.terms?.[0]?.title === 'على الغلاف' ? data?.terms?.[1] : data?.terms?.[0];

    const CategoryValue = data?.tag?.title !== widgetTitle?.trim() ? data?.tag : TermValue;

    return (
        <>
            <div
                className={cn(
                    'relative w-full group overflow-hidden home-section rounded-t-3xl',
                    className
                )}
            >
                <div className="relative w-full h-fit overflow-hidden">
                    {length && template !== 'single' && (
                        <>
                            <NavControl
                                position="right"
                                length={length}
                                setCurrent={setCurrentIndex}
                                className="h-[170px] flex items-center px-3"
                            />
                            <NavControl
                                position="left"
                                length={length}
                                setCurrent={setCurrentIndex}
                                className="h-[170px] flex items-center px-3"
                            />
                        </>
                    )}

                    <div className={cn('overflow-hidden w-full block relative rounded-t-3xl')}>
                        {template !== 'text-outside' && template !== 'single' && CategoryValue ? (
                            <Category
                                color={colorTheme || data?.tag?.color}
                                item={isMainTerm && forceTerm ? forceTerm : CategoryValue}
                                className={cn(
                                    'hidden mb-0 max-md:flex absolute z-[2] top-1.5 right-0 mr-3 py-1'
                                )}
                                paintBg
                            />
                        ) : (
                            ''
                        )}

                        {template !== 'text-outside' ? (
                            <div
                                className={cn(
                                    'flex flex-col items-start overflow-hidden w-full',
                                    'absolute bottom-3 max-md:bottom-7 max-lg:bottom-4 md:bottom-8 right-0',
                                    'max-md:pr-3 pr-6 py-2 max-md:py-0 z-[2]',
                                    'lg:max-w-[93%] max-w-[96%]',
                                    template === 'single' && 'max-md:bottom-2.5'
                                )}
                            >
                                {CategoryValue ? (
                                    <Category
                                        color={colorTheme || data?.tag?.color}
                                        item={isMainTerm && forceTerm ? forceTerm : CategoryValue}
                                        className={cn(
                                            'max-lg:mb-1 max-lg:py-[3px] ',
                                            template !== 'single' && 'max-md:hidden'
                                        )}
                                        paintBg
                                    />
                                ) : (
                                    <label className="h-[22px]" />
                                )}

                                <Link href={postHref(data, ' ')}>
                                    <h1
                                        aria-label={data?.title}
                                        className={cn(
                                            'select-none text-white font-semibold sm:font-normal cursor-pointer w-fit',
                                            'text-6xl max-sm:text-2xl line-clamp-2 max-lg:leading-[1.15] leading-[1.1] max-sm:leading-[1.1]'
                                        )}
                                    >
                                        {data?.title}
                                    </h1>
                                </Link>
                                {/* {data?.author?.name ? <Author item={data?.author} /> : ""} */}
                            </div>
                        ) : (
                            ''
                        )}
                        <Link
                            href={postHref(data, ' ')}
                            className={cn(template !== 'text-outside' && 'smooth-image-gradient')}
                        >
                            <ImageUI
                                data={data}
                                cropSize={'wide'}
                                resolution={resolution ? resolution : 3.3}
                                priority={priority}
                                className=""
                            />
                        </Link>
                    </div>
                    {template === 'single' ? (
                        <div
                            className={cn(
                                'py-3 px-2 bg-lightgreyBackground hidden max-md:flex items-center justify-between w-full h-fit mt-auto'
                            )}
                        >
                            <Author post={data} className={cn('')} />

                            <Time item={dateToShow} className={cn('mr-auto')} />
                        </div>
                    ) : (
                        ''
                    )}
                    {template !== 'single' ? (
                        <div
                            dir="ltr"
                            className="absolute bottom-2.5 left-0 w-full bg-gradient-to-b z-[3] flex items-center flex-row justify-center"
                        >
                            {Array.from({ length: length ?? 0 }).map((_, index) => (
                                <div
                                    key={index}
                                    className={cn(
                                        'max-md:h-2.5 max-md:w-2.5 h-3.5 w-3.5 bg-white/30 rounded-full cursor-pointer',
                                        currentIndex === index && ' bg-white',
                                        length && index !== length - 1 && 'mr-2.5 lg:mr-4'
                                    )}
                                    onClick={() => setActive(index)}
                                />
                            ))}
                        </div>
                    ) : (
                        ''
                    )}
                </div>
                {template === 'text-outside' ? (
                    <div className="relative bg-greyBackground px-4 py-3 max-md:py-0 flex flex-col rtl:items-start ltr:items-end overflow-hidden w-full">
                        {CategoryValue ? (
                            <Category
                                color={colorTheme || data?.tag?.color}
                                item={isMainTerm && forceTerm ? forceTerm : CategoryValue}
                                className="bg-primary border-primary select-none"
                            />
                        ) : (
                            ''
                        )}

                        <Link
                            href={postHref(data)}
                            aria-label={data?.title}
                            className={cn(
                                'select-none font-semibold sm:font-normal cursor-pointer w-fit text-4xl max-sm:text-2xl line-clamp-2 md:line-clamp-1',
                                customText
                            )}
                        >
                            {data?.title}
                        </Link>
                        {data?.summary ? (
                            <Summary
                                className={cn(
                                    'font-amine-light text-xl line-clamp-1',
                                    'rtl:lg:pl-6 rtl:pl-1.5 ltr:lg:pr-6 ltr:pr-1.5',
                                    'text-[#545454]'
                                )}
                            >
                                {data?.summary}
                            </Summary>
                        ) : (
                            <div className="h-7 mt-1" />
                        )}
                    </div>
                ) : (
                    ''
                )}
            </div>
        </>
    );
};
